import React, { useState } from "react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { Sprout, FlaskConical, Leaf, Coins, AlertTriangle } from "lucide-react";
import { ChemicalDetectionView } from "./ChemicalDetectionView";
import { NutrientPanel } from "./NutrientPanel";
import { HVIPanel } from "./hvi/HVIPanel";

const chartStyle = { background: "#0A1128", border: "1px solid rgba(255,255,255,0.1)", fontFamily: "JetBrains Mono", fontSize: 12, color: "#F8FAFC" };

const IndexCard = ({ label, value, max = 1, real }) => {
  const pct = Math.min(100, (Math.max(0, value) / max) * 100);
  return (
    <div className="qs-panel p-4" data-testid={`index-${label.toLowerCase()}`}>
      <div className="flex justify-between items-baseline">
        <span className="text-xs uppercase tracking-widest text-slate-500">{label}{real && <span className="ml-1.5 text-[9px] text-[#00FF7F] border border-[#00FF7F]/50 px-1" data-testid={`real-badge-${label.toLowerCase()}`}>REAL</span>}</span>
        <span className="font-mono text-lg font-bold text-[#00FF7F]">{value}</span>
      </div>
      <div className="h-1.5 bg-[#050A15] mt-3">
        <div className="h-full bg-gradient-to-r from-[#00FFFF] to-[#00FF7F]" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
};

const HEAT = { ndvi: "NDVI", savi: "SAVI", evi: "EVI", ndmi: "NDMI moisture" };

const Heatmap = ({ grid, moisture }) => {
  const color = (v) => {
    if (moisture) return v > 0.3 ? "#00FFFF" : v > 0.1 ? "#38bdf8" : v > 0 ? "#F59E0B" : v > -0.2 ? "#F97316" : "#EF4444";
    if (v > 0.7) return "#00FF7F";
    if (v > 0.55) return "#84cc16";
    if (v > 0.4) return "#F59E0B";
    if (v > 0.25) return "#F97316";
    return "#EF4444";
  };
  return (
    <div className="grid gap-0.5" style={{ gridTemplateColumns: `repeat(${grid[0].length}, 1fr)` }} data-testid="ndvi-heatmap">
      {grid.flatMap((row, ri) => row.map((v, ci) => (
        <div key={`${ri}-${ci}`} title={`NDVI ${v}`} style={{ background: color(v), aspectRatio: "1" }} className="opacity-90" />
      )))}
    </div>
  );
};

export const AgricultureView = ({ result, scanId, onUpdated }) => {
  const { indices, pesticide, carbon, spectral_curve } = result;
  const chemColor = { Clean: "#10B981", Elevated: "#F59E0B", Contaminated: "#EF4444" }[pesticide.status];
  const real = result.indices_real?.indices || {};
  const heatmaps = result.heatmaps || { ndvi: result.heatmap };
  const [heat, setHeat] = useState("ndvi");

  return (
    <div className="space-y-6" data-testid="agriculture-view">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <IndexCard label="NDVI" value={indices.ndvi} real={"ndvi" in real} />
        <IndexCard label="SAVI" value={indices.savi} real={"savi" in real} />
        <IndexCard label="EVI" value={indices.evi} real={"evi" in real} />
        {indices.ndmi != null && <IndexCard label="NDMI" value={indices.ndmi} real={"ndmi" in real} />}
        <IndexCard label="Chlorophyll" value={indices.chlorophyll} max={60} />
      </div>
      {result.indices_real && (
        <div className="text-[11px] font-mono text-[#00FF7F] flex flex-wrap gap-x-4 gap-y-1" data-testid="real-indices-note">
          <span>REAL indices · {result.indices_real.source}</span>
          {Object.entries(result.indices_real.stats).map(([k, s]) => <span key={k} className="text-slate-400">{k.toUpperCase()} {s.min}…{s.max}</span>)}
          {result.indices_real.moisture_stress_fraction != null && <span className="text-[#00FFFF]">moisture-stressed pixels {Math.round(result.indices_real.moisture_stress_fraction * 100)}%</span>}
        </div>
      )}

      <div className="grid md:grid-cols-3 gap-4">
        <div className="qs-panel p-5">
          <div className="flex items-center gap-2 text-sm text-slate-400"><Sprout className="h-4 w-4 text-[#00FF7F]" /> Crop Health</div>
          <div className="font-display font-bold text-2xl mt-2">{result.crop_health}</div>
          <div className="text-xs text-slate-500 mt-1 font-mono">Stress {result.stress_percent}% · Disease {result.disease_risk}%</div>
        </div>
        <div className="qs-panel p-5">
          <div className="flex items-center gap-2 text-sm text-slate-400"><Leaf className="h-4 w-4 text-[#00FFFF]" /> Yield Potential</div>
          <div className="font-display font-bold text-2xl mt-2 font-mono">{result.yield_potential_t_ha} <span className="text-sm text-slate-500">t/ha</span></div>
        </div>
        <div className="qs-panel p-5" style={{ borderColor: `${chemColor}55` }} data-testid="pesticide-card">
          <div className="flex items-center gap-2 text-sm text-slate-400"><FlaskConical className="h-4 w-4" style={{ color: chemColor }} /> Chemical / Pesticide</div>
          <div className="font-display font-bold text-2xl mt-2" style={{ color: chemColor }}>{pesticide.status}</div>
          <div className="text-xs text-slate-500 mt-1 font-mono">Load {pesticide.chemical_load_index} · {pesticide.contamination_zones} zone(s)</div>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-5">
        <div className="qs-panel p-5">
          <h4 className="font-display font-semibold mb-4">Spectral Reflectance Curve</h4>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={spectral_curve}>
              <CartesianGrid stroke="#1e293b" strokeDasharray="3 3" />
              <XAxis dataKey="wavelength" stroke="#475569" tick={{ fontSize: 11, fontFamily: "JetBrains Mono" }} unit="nm" />
              <YAxis stroke="#475569" tick={{ fontSize: 11, fontFamily: "JetBrains Mono" }} />
              <Tooltip contentStyle={chartStyle} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Line type="monotone" dataKey="reflectance" stroke="#00FFFF" strokeWidth={2} dot={false} name="Measured" />
              <Line type="monotone" dataKey="healthy_reference" stroke="#8A2BE2" strokeWidth={1.5} strokeDasharray="4 4" dot={false} name="Healthy ref" />
            </LineChart>
          </ResponsiveContainer>
        </div>
        <div className="qs-panel p-5">
          <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
            <h4 className="font-display font-semibold">{HEAT[heat]} Field Heatmap</h4>
            {Object.keys(heatmaps).length > 1 && (
              <div className="flex gap-1" data-testid="heatmap-tabs">
                {Object.keys(heatmaps).map((k) => <button key={k} data-testid={`heatmap-tab-${k}`} onClick={() => setHeat(k)} className={`text-[10px] font-mono uppercase px-2 py-0.5 border ${heat === k ? "border-[#00FF7F] text-[#00FF7F]" : "border-white/10 text-slate-500"}`}>{k}</button>)}
              </div>
            )}
          </div>
          <Heatmap grid={heatmaps[heat] || result.heatmap} moisture={heat === "ndmi"} />
          <div className="flex justify-between text-[10px] font-mono text-slate-500 mt-2">
            <span>{heat === "ndmi" ? "Dry / stressed" : "Low vigor"}</span><span>{heat === "ndmi" ? "Moist canopy" : "High vigor"}</span>
          </div>
        </div>
      </div>

      <NutrientPanel nutrients={result.nutrients} scanId={scanId} onUpdated={onUpdated} />
      {scanId && result.nutrients && <HVIPanel scanId={scanId} nutrientsVerified={`${result.nutrients.anchor}|${result.nutrients.probe?.id || ""}`} />}
      <ChemicalDetectionView pesticide={pesticide} />

      <div className="qs-grad-border p-6" data-testid="carbon-card">
        <div className="flex items-center gap-2 text-[#00FF7F]"><Coins className="h-5 w-5" /> <span className="text-xs uppercase tracking-widest">Zero-Carbon Credits</span></div>
        <div className="flex flex-wrap items-baseline gap-6 mt-3">
          <div><span className="font-mono text-3xl font-bold text-[#00FF7F]">{carbon.credits}</span> <span className="text-slate-400">ZCC minted</span></div>
          <div className="text-sm text-slate-400 font-mono">{carbon.sequestration_tco2e} tCO₂e sequestered</div>
        </div>
        <p className="text-xs text-slate-500 mt-2">{carbon.methodology}</p>
      </div>

      {pesticide.status !== "Clean" && (
        <div className="flex items-start gap-3 bg-[#F59E0B]/10 border border-[#F59E0B]/30 p-4 text-sm">
          <AlertTriangle className="h-5 w-5 text-[#F59E0B] shrink-0" />
          <p className="text-slate-300">Elevated chemical signatures detected via non-destructive spectral testing. Consider targeted remediation and reduced agrochemical application.</p>
        </div>
      )}
    </div>
  );
};
